import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { periodsAPI, goalsAPI, kpisAPI, auditAPI, resultsAPI } from '../lib/api';
import { useKPIOrder } from '../lib/useKPIOrder';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Calendar,
  CalendarDays,
  Target,
  History,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Edit,
  Plus,
  GitBranch,
  ChevronDown,
  ChevronRight,
  Lock,
  Trash2,
  Filter,
  RefreshCw,
  ShieldAlert,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, getBimesterName, formatNumber, cn, getPeriodicityLabel, getPeriodicityOptions } from '../lib/utils';

// Helper to get bucket options based on periodicity
// Month names for dynamic label generation
const MONTH_NAMES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const MONTH_NAMES_FULL = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

// Generate bucket options dynamically based on periodicity and start month
const getBucketOptions = (periodicity, startMonth = 1, goalPeriodicity = 'bimonthly') => {
  // Validate startMonth (1-12)
  const effectiveStartMonth = (startMonth && startMonth >= 1 && startMonth <= 12) ? startMonth : 1;
  
  switch (periodicity) {
    case 'monthly':
      // Monthly always shows all 12 months, regardless of start month
      return MONTH_NAMES_FULL.map((name, idx) => ({
        value: idx + 1,
        label: name,
      }));
      
    case 'quarterly':
      // Generate 4 quarters starting from the configured start month
      return [1, 2, 3, 4].map((quarter) => {
        const quarterStartMonth = ((effectiveStartMonth - 1 + (quarter - 1) * 3) % 12);
        // End month is 2 months after start month (3 months total: start + 2)
        const quarterEndMonth = ((quarterStartMonth + 2) % 12);
        const startName = MONTH_NAMES[quarterStartMonth];
        const endName = MONTH_NAMES[quarterEndMonth];
        return {
          value: quarter,
          label: `Trimestre ${quarter} (${startName}-${endName})`,
        };
      });
      
    case 'semiannual':
      // Generate 2 semesters starting from the configured start month
      return [1, 2].map((semester) => {
        const semStartMonth = ((effectiveStartMonth - 1 + (semester - 1) * 6) % 12);
        // End month is 5 months after start month (6 months total: start + 5)
        const semEndMonth = ((semStartMonth + 5) % 12);
        const startName = MONTH_NAMES[semStartMonth];
        const endName = MONTH_NAMES[semEndMonth];
        const semesterName = semester === 1 ? 'Primer' : 'Segundo';
        return {
          value: semester,
          label: `${semesterName} Semestre (${startName}-${endName})`,
        };
      });
      
    case 'annual':
      return [
        { value: 1, label: 'Anual' },
      ];
      
    case 'bimonthly':
      // Generate 6 bimesters (bimonthly doesn't use custom start month)
      return [1, 2, 3, 4, 5, 6].map((bim) => {
        const bimStartMonth = (bim - 1) * 2;
        const bimEndMonth = bimStartMonth + 1;
        const startName = MONTH_NAMES[bimStartMonth];
        const endName = MONTH_NAMES[bimEndMonth];
        return {
          value: bim,
          label: `Bimestre ${bim} (${startName}-${endName})`,
        };
      });
      
    case 'weekly':
    default:
      // Weekly uses goal_periodicity to determine grouping
      if (goalPeriodicity === 'quarterly') {
        return [
          { value: 1, label: 'Trimestre 1 (Ene-Mar)' },
          { value: 2, label: 'Trimestre 2 (Abr-Jun)' },
          { value: 3, label: 'Trimestre 3 (Jul-Sep)' },
          { value: 4, label: 'Trimestre 4 (Oct-Dic)' },
        ];
      }
      if (goalPeriodicity === 'monthly') {
        const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
        return months.map((m, i) => ({ value: i + 1, label: m }));
      }
      return [
        { value: 1, label: 'Bimestre 1 (Ene-Feb)' },
        { value: 2, label: 'Bimestre 2 (Mar-Abr)' },
        { value: 3, label: 'Bimestre 3 (May-Jun)' },
        { value: 4, label: 'Bimestre 4 (Jul-Ago)' },
        { value: 5, label: 'Bimestre 5 (Sep-Oct)' },
        { value: 6, label: 'Bimestre 6 (Nov-Dic)' },
      ];
  }
};

// Get label for bucket based on periodicity and start month
const getBucketLabel = (periodicity, bucketIndex, startMonth = 1, goalPeriodicity = 'bimonthly') => {
  const options = getBucketOptions(periodicity, startMonth, goalPeriodicity);
  const option = options.find(o => o.value === bucketIndex);
  return option?.label || `Bucket ${bucketIndex}`;
};

// Get term label based on periodicity (for UI labels)
const getBucketTermLabel = (periodicity, goalPeriodicity = 'bimonthly') => {
  switch (periodicity) {
    case 'monthly':
      return 'Mes';
    case 'quarterly':
      return 'Trimestre';
    case 'semiannual':
      return 'Semestre';
    case 'annual':
      return 'Año';
    case 'bimonthly':
      return 'Bimestre';
    case 'weekly':
    default:
      return goalPeriodicity === 'quarterly' ? 'Trimestre' : (goalPeriodicity === 'monthly' ? 'Mes' : 'Bimestre');
  }
};

const Settings = () => {
  const navigate = useNavigate();
  const { isViewer, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [periods, setPeriods] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [goals, setGoals] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [selectedYear, setSelectedYear] = useState(2026);
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
  const [isGoalDialogOpen, setIsGoalDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [savingGoal, setSavingGoal] = useState(false);
  const [goalForm, setGoalForm] = useState({
    kpi_id: '',
    year: 2026,
    bimester: 1,
    bimester_goal: '',  // String vacío para mejor UX en input
    goal_range_min: '',  // Para tipo 'range'
    goal_range_max: '',  // Para tipo 'range'
    node_id: null,
  });
  const [expandedKPIs, setExpandedKPIs] = useState({});
  const [selectedKpiForGoal, setSelectedKpiForGoal] = useState(null);
  
  // Historical goal edit state
  const [goalEditReason, setGoalEditReason] = useState('');
  const [goalEditLabel, setGoalEditLabel] = useState('accion_tomada');
  const [isHistoricalGoal, setIsHistoricalGoal] = useState(false);
  
  // Tab state - controlled to prevent reset on data refresh
  const [activeTab, setActiveTab] = useState('calendar');
  
  // Calendar configuration state
  const [calendarConfig, setCalendarConfig] = useState(null);
  const [configForm, setConfigForm] = useState({
    initial_per_number: 3,
    start_date: '',
    initial_per_type: 'special',
    special_duration: 3,
  });
  const [isRegenerating, setIsRegenerating] = useState(false);
  
  // Delete goal state
  const [deleteGoalDialogOpen, setDeleteGoalDialogOpen] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState(null);
  const [deletingGoal, setDeletingGoal] = useState(false);

  // Apply saved KPI order from KPIs page (single source of truth)
  const { orderedKpis: displayKpis } = useKPIOrder(kpis);

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [periodsRes, kpisRes, goalsRes, auditRes, configRes] = await Promise.all([
        periodsAPI.getAll({ year: selectedYear }),
        kpisAPI.getAll(),
        goalsAPI.getAll({ year: selectedYear }),
        auditAPI.getLogs({ limit: 50 }),
        periodsAPI.getConfig(selectedYear),
      ]);
      setPeriods(periodsRes.data);
      setKpis(kpisRes.data);
      setGoals(goalsRes.data);
      setAuditLogs(auditRes.data);
      setCalendarConfig(configRes.data);
      
      // If config exists, populate the form
      if (configRes.data) {
        setConfigForm({
          initial_per_number: configRes.data.initial_per_number,
          start_date: configRes.data.start_date,
          initial_per_type: configRes.data.initial_per_type,
          special_duration: configRes.data.special_duration || 3,
        });
      } else {
        // Reset to defaults
        setConfigForm({
          initial_per_number: 3,
          start_date: `${selectedYear}-01-05`,
          initial_per_type: 'special',
          special_duration: 3,
        });
      }
    } catch (error) {
      console.error('Error fetching settings data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePeriods = async () => {
    setGenerating(true);
    try {
      const config = {
        initial_per_number: configForm.initial_per_number,
        start_date: configForm.start_date,
        initial_per_type: configForm.initial_per_type,
        special_duration: configForm.initial_per_type === 'special' ? configForm.special_duration : null,
      };
      const response = await periodsAPI.generate(selectedYear, config);
      toast.success(response.data.message);
      setIsGenerateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar periodos');
    } finally {
      setGenerating(false);
    }
  };
  
  const handleRegeneratePeriods = async () => {
    setIsRegenerating(true);
    try {
      const config = {
        initial_per_number: configForm.initial_per_number,
        start_date: configForm.start_date,
        initial_per_type: configForm.initial_per_type,
        special_duration: configForm.initial_per_type === 'special' ? configForm.special_duration : null,
      };
      const response = await periodsAPI.regenerate(selectedYear, config);
      toast.success(response.data.message);
      setIsGenerateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al regenerar periodos');
    } finally {
      setIsRegenerating(false);
    }
  };
  
  const openGenerateDialog = () => {
    // Set default start date for the selected year if not set
    if (!configForm.start_date) {
      setConfigForm(prev => ({ ...prev, start_date: `${selectedYear}-01-05` }));
    }
    setIsGenerateDialogOpen(true);
  };

  // Get goals grouped by KPI for hierarchical display
  const getGoalsGroupedByKPI = () => {
    const grouped = {};
    kpis.forEach(kpi => {
      const kpiGoals = goals.filter(g => g.kpi_id === kpi.id);
      if (kpiGoals.length > 0 || (kpi.has_hierarchy && kpi.hierarchy_nodes?.length > 0)) {
        grouped[kpi.id] = {
          kpi,
          goals: kpiGoals,
          hasHierarchy: kpi.has_hierarchy && kpi.hierarchy_nodes?.length > 0
        };
      }
    });
    return grouped;
  };

  // For hierarchical KPIs, get leaf nodes that still need goals for a bimester
  const getLeafNodesWithoutGoal = (kpi, bimester) => {
    if (!kpi.has_hierarchy || !kpi.hierarchy_nodes) return [];
    const leafNodes = kpi.hierarchy_nodes.filter(n => n.is_leaf);
    const existingGoalNodeIds = goals
      .filter(g => g.kpi_id === kpi.id && g.bimester === bimester && !g.is_calculated)
      .map(g => g.node_id);
    return leafNodes.filter(n => !existingGoalNodeIds.includes(n.id));
  };

  // Delete goal functions
  const openDeleteGoalDialog = (goal) => {
    const kpi = kpis.find(k => k.id === goal.kpi_id);
    setGoalToDelete({ ...goal, kpiName: kpi?.name || 'KPI desconocido' });
    setDeleteGoalDialogOpen(true);
  };

  const handleDeleteGoal = async () => {
    if (!goalToDelete) return;
    setDeletingGoal(true);
    try {
      await goalsAPI.delete(goalToDelete.id);
      toast.success('Meta eliminada correctamente');
      setDeleteGoalDialogOpen(false);
      setGoalToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar la meta');
    } finally {
      setDeletingGoal(false);
    }
  };

  const handleOpenGoalDialog = async (goal = null, kpi = null, nodeId = null) => {
    // Reset historical goal state
    setGoalEditReason('');
    setGoalEditLabel('accion_tomada');
    setIsHistoricalGoal(false);
    
    if (goal) {
      // Editing existing goal - check if it's historical (has results)
      try {
        const resultsRes = await resultsAPI.getAll({ 
          kpi_id: goal.kpi_id,
          year: goal.year
        });
        
        // Check if any results exist for the periods in this bimester
        const bimesterPeriods = periods.filter(p => p.bimester === goal.bimester && p.year === goal.year);
        const bimesterPeriodIds = bimesterPeriods.map(p => p.id);
        
        const hasResults = resultsRes.data.some(r => {
          const matchesPeriod = bimesterPeriodIds.includes(r.period_id);
          const matchesNode = goal.node_id ? r.node_id === goal.node_id : !r.node_id;
          return matchesPeriod && matchesNode;
        });
        
        setIsHistoricalGoal(hasResults);
      } catch (error) {
        console.error('Error checking if goal is historical:', error);
        // Assume it's historical to be safe
        setIsHistoricalGoal(true);
      }
      
      setEditingGoal(goal);
      setGoalForm({
        kpi_id: goal.kpi_id,
        year: goal.year,
        bimester: goal.bimester,
        bimester_goal: goal.bimester_goal.toString(),  // Convert to string for input
        goal_range_min: goal.goal_range?.min != null ? goal.goal_range.min.toString() : '',
        goal_range_max: goal.goal_range?.max != null ? goal.goal_range.max.toString() : '',
        node_id: goal.node_id,
      });
      setSelectedKpiForGoal(kpis.find(k => k.id === goal.kpi_id));
    } else if (kpi && nodeId) {
      // Creating goal for a specific node in hierarchical KPI
      setEditingGoal(null);
      setGoalForm({ 
        kpi_id: kpi.id, 
        year: selectedYear, 
        bimester: 1, 
        bimester_goal: '',  // Empty string for new goals
        goal_range_min: '',
        goal_range_max: '',
        node_id: nodeId
      });
      setSelectedKpiForGoal(kpi);
    } else {
      // Creating new goal (will select KPI)
      setEditingGoal(null);
      setGoalForm({ kpi_id: '', year: selectedYear, bimester: 1, bimester_goal: '', goal_range_min: '', goal_range_max: '', node_id: null });
      setSelectedKpiForGoal(null);
    }
    setIsGoalDialogOpen(true);
  };

  const handleKpiSelect = (kpiId) => {
    const kpi = kpis.find(k => k.id === kpiId);
    setSelectedKpiForGoal(kpi);
    setGoalForm({ ...goalForm, kpi_id: kpiId, node_id: null });
  };

  const handleSaveGoal = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Validate reason for historical goals
    if (editingGoal && isHistoricalGoal && (!goalEditReason || goalEditReason.trim().length < 5)) {
      toast.error('Se requiere un motivo de al menos 5 caracteres para modificar metas históricas');
      return;
    }
    
    // Validate range goal
    const isRangeGoal = selectedKpiForGoal?.goal_calculation_type === 'range';
    let goalRange = null;
    if (isRangeGoal) {
      const minV = goalForm.goal_range_min === '' ? null : parseFloat(goalForm.goal_range_min);
      const maxV = goalForm.goal_range_max === '' ? null : parseFloat(goalForm.goal_range_max);
      if (minV === null || maxV === null || isNaN(minV) || isNaN(maxV)) {
        toast.error('Ingresa un mínimo y un máximo válidos para la meta por rango');
        return;
      }
      if (minV >= maxV) {
        toast.error('El mínimo debe ser menor que el máximo');
        return;
      }
      goalRange = { min: minV, max: maxV };
    }
    
    setSavingGoal(true);
    try {
      // Convert empty string to 0, otherwise parse as float for decimal support
      // Para tipo 'range' la meta numérica no aplica → se guarda 0
      const goalValue = isRangeGoal ? 0 : (goalForm.bimester_goal === '' ? 0 : parseFloat(goalForm.bimester_goal) || 0);
      
      const payload = {
        kpi_id: goalForm.kpi_id,
        year: goalForm.year,
        bimester: goalForm.bimester,
        bimester_goal: goalValue,
        goal_range: goalRange,
        node_id: goalForm.node_id || null,
      };

      if (editingGoal) {
        // Add reason and label for historical goal edits
        if (isHistoricalGoal) {
          payload.reason = goalEditReason.trim();
          payload.comment_label = goalEditLabel;
        }
        await goalsAPI.update(editingGoal.id, payload);
        toast.success('Meta actualizada correctamente');
      } else {
        await goalsAPI.create(payload);
        toast.success('Meta creada correctamente');
      }
      
      // Close ONLY the goal dialog, not anything else
      setIsGoalDialogOpen(false);
      setEditingGoal(null);
      setSelectedKpiForGoal(null);
      setGoalEditReason('');
      setGoalEditLabel('accion_tomada');
      setIsHistoricalGoal(false);
      
      // Refresh goals data without affecting tab state
      // We fetch only goals to minimize re-renders
      const goalsRes = await goalsAPI.getAll({ year: selectedYear });
      setGoals(goalsRes.data);
      
    } catch (error) {
      // Handle 409: existing goal that can be edited
      if (error.response?.status === 409 && error.response?.data?.detail?.existing_goal) {
        const existingGoal = error.response.data.detail.existing_goal;
        setEditingGoal(existingGoal);
        setGoalForm({
          kpi_id: existingGoal.kpi_id,
          year: existingGoal.year,
          bimester: existingGoal.bimester,
          bimester_goal: existingGoal.bimester_goal,
          goal_range_min: existingGoal.goal_range?.min != null ? existingGoal.goal_range.min.toString() : '',
          goal_range_max: existingGoal.goal_range?.max != null ? existingGoal.goal_range.max.toString() : '',
          node_id: existingGoal.node_id || '',
        });
        toast.info('Se encontró una meta existente. Puede editarla a continuación.');
        return;
      }
      // On error, do NOT close the modal - let user fix and retry
      toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Error al guardar la meta');
    } finally {
      setSavingGoal(false);
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      create: 'Creación',
      update: 'Actualización',
      delete: 'Eliminación',
      toggle_status: 'Cambio de estado',
      assign_kpis: 'Asignación de KPIs',
      generate: 'Generación',
    };
    return labels[action] || action;
  };

  const getEntityLabel = (type) => {
    const labels = {
      kpi: 'KPI',
      user: 'Usuario',
      goal: 'Meta',
      result: 'Resultado',
      periods: 'Periodos',
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-96" />
        </div>
      </Layout>
    );
  }

  // Block access for Viewer role
  if (isViewer()) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="settings-access-denied">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-8 max-w-md text-center">
            <ShieldAlert className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Acceso Restringido</h2>
            <p className="text-slate-600 mb-4">
              Tu rol de <strong>Viewer</strong> no tiene permisos para acceder a la configuración del sistema.
            </p>
            <p className="text-sm text-slate-500 mb-6">
              Solo puedes visualizar el Dashboard y los KPIs asignados a tu usuario.
            </p>
            <Button onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard-btn">
              Ir al Dashboard
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6 max-w-full overflow-x-hidden" data-testid="settings-page">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">Configuración</h1>
            <p className="text-slate-500 mt-1 text-sm sm:text-base">Calendario, metas y auditoría del sistema</p>
          </div>
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-28 sm:w-32" data-testid="settings-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2026">2026</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
              <SelectItem value="2027">2027</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="w-full sm:w-auto flex-wrap h-auto p-1" data-testid="settings-tabs">
            <TabsTrigger value="calendar" className="flex-1 sm:flex-none text-xs sm:text-sm py-2">
              <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Calendario</span>
              <span className="xs:hidden">Cal</span>
            </TabsTrigger>
            <TabsTrigger value="goals" className="flex-1 sm:flex-none text-xs sm:text-sm py-2">
              <Target className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Metas
            </TabsTrigger>
            <TabsTrigger value="audit" className="flex-1 sm:flex-none text-xs sm:text-sm py-2">
              <History className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Auditoría</span>
              <span className="xs:hidden">Audit</span>
            </TabsTrigger>
          </TabsList>

          {/* Calendar Tab */}
          <TabsContent value="calendar">
            <Card data-testid="calendar-settings-card">
              <CardHeader>
                <CardTitle className="font-['Manrope'] flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Generación de Periodos
                </CardTitle>
                <CardDescription>
                  Genera automáticamente los periodos (PER) para el año seleccionado
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-slate-900 mb-2">Reglas del calendario:</h4>
                    <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
                      <li><strong>PER 1 (Especial):</strong> 5 al 7 de enero (3 días)</li>
                      <li><strong>PER estándar:</strong> Inicia jueves, termina miércoles (7 días)</li>
                      <li>El periodo se asigna al mes de su día de cierre (miércoles)</li>
                      <li>Bimestres: Ene-Feb, Mar-Abr, May-Jun, Jul-Ago, Sep-Oct, Nov-Dic</li>
                      <li>Edición permitida hasta el sábado posterior al cierre</li>
                    </ul>
                  </div>

                  {periods.length > 0 ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="font-medium text-emerald-800">
                            Periodos generados para {selectedYear}
                          </p>
                          <p className="text-sm text-emerald-600">
                            {periods.length} periodos configurados (PER 3 al PER {periods[periods.length - 1]?.number})
                          </p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={openGenerateDialog}
                          data-testid="regenerate-periods-btn"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Regenerar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-medium text-amber-800">
                          No hay periodos para {selectedYear}
                        </p>
                        <p className="text-sm text-amber-600">
                          Genera los periodos para comenzar a capturar resultados
                        </p>
                      </div>
                      <Button onClick={openGenerateDialog} data-testid="generate-periods-btn">
                        Generar Periodos
                      </Button>
                    </div>
                  )}

                  {/* Show first and last period info */}
                  {periods.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 border border-slate-200 rounded-lg">
                        <p className="text-sm text-slate-500 mb-1">Primer Periodo</p>
                        <p className="font-semibold text-slate-900">PER {periods[0]?.number} {periods[0]?.is_special && '(Especial)'}</p>
                        <p className="text-sm text-slate-600">{formatDate(periods[0]?.start_date)} - {formatDate(periods[0]?.end_date)}</p>
                      </div>
                      <div className="p-4 border border-slate-200 rounded-lg">
                        <p className="text-sm text-slate-500 mb-1">Último Periodo</p>
                        <p className="font-semibold text-slate-900">PER {periods[periods.length - 1]?.number}</p>
                        <p className="text-sm text-slate-600">{formatDate(periods[periods.length - 1]?.start_date)} - {formatDate(periods[periods.length - 1]?.end_date)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Goals Tab */}
          <TabsContent value="goals">
            <Card data-testid="goals-settings-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-['Manrope'] flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Configuración de Metas
                  </CardTitle>
                  <CardDescription>
                    Configura y edita las metas para cada KPI por periodo
                  </CardDescription>
                </div>
                <Button onClick={() => handleOpenGoalDialog()} data-testid="add-goal-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Meta
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {goals.length === 0 && kpis.filter(k => k.has_hierarchy).length === 0 ? (
                  <div className="text-center py-8 sm:py-12 px-4">
                    <Target className="w-10 h-10 sm:w-12 sm:h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 text-sm sm:text-base">No hay metas configuradas para {selectedYear}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {/* Group goals by KPI */}
                    {displayKpis.map((kpi) => {
                      const kpiGoals = goals.filter(g => g.kpi_id === kpi.id);
                      const hasHierarchy = kpi.has_hierarchy && kpi.hierarchy_nodes?.length > 0;
                      const isExpanded = expandedKPIs[kpi.id];
                      
                      // Get KPI periodicity and start month
                      const kpiPeriodicity = kpi.periodicity || 'weekly';
                      const kpiStartMonth = kpi.periodicity_start_month || 1;
                      const kpiGoalPeriodicity = kpi.goal_periodicity || 'bimonthly';
                      const bucketOptions = getBucketOptions(kpiPeriodicity, kpiStartMonth, kpiGoalPeriodicity);
                      const bucketTermLabel = getBucketTermLabel(kpiPeriodicity, kpiGoalPeriodicity);
                      const isWeeklyOrBimonthly = kpiPeriodicity === 'weekly' || kpiPeriodicity === 'bimonthly';
                      
                      // Skip KPIs with no goals and no hierarchy
                      if (kpiGoals.length === 0 && !hasHierarchy) return null;
                      
                      // For hierarchical KPIs, separate leaf and parent goals
                      const leafGoals = hasHierarchy ? kpiGoals.filter(g => !g.is_calculated) : kpiGoals;
                      const parentGoals = hasHierarchy ? kpiGoals.filter(g => g.is_calculated) : [];
                      
                      // Get all buckets that have goals
                      const bucketsWithGoals = [...new Set(kpiGoals.map(g => g.bimester))].sort((a, b) => a - b);
                      
                      // Calculate total goal for display - only leaf goals to avoid double-counting with parent calculated goals
                      const totalGoal = leafGoals.reduce((sum, g) => sum + (g.bimester_goal || 0), 0);
                      
                      return (
                        <div key={kpi.id} className="p-3 sm:p-4" data-testid={`kpi-goals-${kpi.id}`}>
                          {/* KPI Header - Always clickable for expand/collapse */}
                          <div 
                            className="flex items-center gap-3 cursor-pointer group"
                            onClick={() => setExpandedKPIs({...expandedKPIs, [kpi.id]: !isExpanded})}
                          >
                            <div className={cn(
                              "w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors",
                              hasHierarchy ? "bg-violet-100 group-hover:bg-violet-200" : "bg-slate-100 group-hover:bg-slate-200"
                            )}>
                              {hasHierarchy ? (
                                <GitBranch className="w-4 h-4 sm:w-5 sm:h-5 text-violet-600" />
                              ) : (
                                <Target className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                {/* Always show chevron for expand/collapse */}
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-slate-400" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-slate-400" />
                                )}
                                <h3 className="font-medium text-slate-900 text-sm sm:text-base truncate uppercase">{kpi.name}</h3>
                                {/* Show periodicity badge for non-weekly KPIs */}
                                {!isWeeklyOrBimonthly && (
                                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                    <CalendarDays className="w-3 h-3 mr-1" />
                                    {getPeriodicityLabel(kpiPeriodicity)}
                                  </Badge>
                                )}
                                {hasHierarchy && (
                                  <Badge variant="outline" className="text-xs hidden sm:inline-flex">
                                    {kpi.hierarchy_dimension}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs sm:text-sm text-slate-500 uppercase">{kpi.area}</p>
                            </div>
                            
                            {/* Summary badges - Unified for both types */}
                            <div className="flex items-center gap-2">
                              {kpiGoals.length > 0 && (
                                <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                                  {kpiGoals.length} {kpiGoals.length === 1 ? 'meta' : 'metas'}
                                </Badge>
                              )}
                              {bucketsWithGoals.length > 0 && (
                                <Badge variant="outline" className="text-xs hidden sm:inline-flex">
                                  {bucketsWithGoals.map(b => getBucketLabel(kpiPeriodicity, b, kpiStartMonth, kpiGoalPeriodicity).slice(0, 3)).join(', ')}
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          {/* Collapsible content for BOTH simple and hierarchical KPIs */}
                          {isExpanded && (
                            <div className={cn(
                              "mt-3 pl-4 sm:pl-10 space-y-3 border-l-2",
                              hasHierarchy ? "border-violet-200" : "border-slate-200"
                            )}>
                              {/* Simple KPI Goals - Now collapsable */}
                              {!hasHierarchy && kpiGoals.length > 0 && (
                                <div className="space-y-2">
                                  {kpiGoals.map(goal => (
                                    <div 
                                      key={goal.id} 
                                      className="flex items-center justify-between gap-2 p-2 sm:p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                                      data-testid={`goal-row-${goal.id}`}
                                    >
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="text-sm font-medium text-slate-900">
                                            {getBucketLabel(kpiPeriodicity, goal.bimester, kpiStartMonth, kpiGoalPeriodicity)}
                                          </span>
                                          <Badge variant="outline" className="text-xs bg-slate-100">
                                            {kpi.goal_calculation_type === 'range' && goal.goal_range
                                              ? `Rango: ${formatNumber(goal.goal_range.min)} – ${formatNumber(goal.goal_range.max)}`
                                              : `Total: ${formatNumber(goal.bimester_goal)}`}
                                          </Badge>
                                        </div>
                                        {/* Show per_goals only for weekly/bimonthly KPIs (no aplica a rango) */}
                                        {kpi.goal_calculation_type !== 'range' && isWeeklyOrBimonthly && goal.per_goals && goal.per_goals.length > 0 && (
                                          <div className="flex flex-wrap gap-1">
                                            {goal.per_goals.map((pg, idx) => (
                                              <Badge key={idx} variant="secondary" className="text-xs bg-slate-50 text-slate-600">
                                                PER {pg.period_number}: {formatNumber(pg.goal)}
                                              </Badge>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-1 flex-shrink-0">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 w-8 p-0"
                                          onClick={(e) => { e.stopPropagation(); handleOpenGoalDialog(goal); }}
                                          data-testid={`edit-goal-${goal.id}`}
                                        >
                                          <Edit className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 w-8 p-0 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                          onClick={(e) => { e.stopPropagation(); openDeleteGoalDialog(goal); }}
                                          data-testid={`delete-goal-${goal.id}`}
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {/* No goals message for simple KPIs */}
                              {!hasHierarchy && kpiGoals.length === 0 && (
                                <div className="text-center py-4 text-slate-500 text-sm">
                                  No hay metas configuradas para este KPI
                                </div>
                              )}
                              
                              {/* Hierarchical KPI Goals */}
                              {hasHierarchy && (
                                <>
                                  {/* Bucket selector for adding goals - dynamic based on periodicity */}
                                  {bucketOptions.map(bucketOption => {
                                    const bucketIndex = bucketOption.value;
                                    const bucketLeafGoals = leafGoals.filter(g => g.bimester === bucketIndex);
                                    const bucketParentGoals = parentGoals.filter(g => g.bimester === bucketIndex);
                                    const leafNodes = kpi.hierarchy_nodes.filter(n => n.is_leaf);
                                    const nodesWithGoal = bucketLeafGoals.map(g => g.node_id);
                                    const nodesWithoutGoal = leafNodes.filter(n => !nodesWithGoal.includes(n.id));
                                    
                                    // Skip buckets with no goals and no pending nodes
                                    if (bucketLeafGoals.length === 0 && nodesWithoutGoal.length === 0) return null;
                                    
                                    return (
                                      <div key={bucketIndex} className="space-y-2">
                                        <div className="flex items-center justify-between">
                                          <h4 className="text-sm font-medium text-slate-700">{bucketOption.label}</h4>
                                          {nodesWithoutGoal.length > 0 && (
                                            <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700">
                                              {nodesWithoutGoal.length} sin meta
                                            </Badge>
                                          )}
                                        </div>
                                        
                                        {/* Show parent (calculated) goals */}
                                        {bucketParentGoals.map(goal => (
                                          <div 
                                            key={goal.id}
                                            className="flex items-center gap-2 p-2 bg-slate-100 rounded border border-slate-200"
                                            data-testid={`goal-parent-${goal.id}`}
                                          >
                                            <Lock className="w-3 h-3 text-slate-400" />
                                            <span className="text-sm font-medium text-slate-600">{goal.node_name}</span>
                                            <span className="text-xs text-slate-400">(calculado)</span>
                                            <span className="ml-auto font-semibold">{formatNumber(goal.bimester_goal)}</span>
                                          </div>
                                        ))}
                                        
                                        {/* Show leaf goals */}
                                        {bucketLeafGoals.map(goal => {
                                          const node = kpi.hierarchy_nodes.find(n => n.id === goal.node_id);
                                          const parentNode = node ? kpi.hierarchy_nodes.find(n => n.id === node.parent_id) : null;
                                      
                                      return (
                                        <div 
                                          key={goal.id}
                                          className="flex items-center gap-2 p-2 bg-white rounded border border-slate-200"
                                          data-testid={`goal-leaf-${goal.id}`}
                                        >
                                          <div className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0" />
                                          <div className="flex-1 min-w-0">
                                            <span className="text-sm font-medium">{goal.node_name || node?.name}</span>
                                            {parentNode && (
                                              <span className="text-xs text-slate-400 ml-2">({parentNode.name})</span>
                                            )}
                                          </div>
                                          <span className="font-semibold">{formatNumber(goal.bimester_goal)}</span>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 w-7 p-0"
                                            onClick={() => handleOpenGoalDialog(goal)}
                                          >
                                            <Edit className="w-3 h-3" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                            onClick={() => openDeleteGoalDialog(goal)}
                                          >
                                            <Trash2 className="w-3 h-3" />
                                          </Button>
                                        </div>
                                      );
                                    })}
                                    
                                    {/* Show nodes without goals */}
                                    {nodesWithoutGoal.map(node => {
                                      const parentNode = kpi.hierarchy_nodes.find(n => n.id === node.parent_id);
                                      
                                      return (
                                        <div 
                                          key={node.id}
                                          className="flex items-center gap-2 p-2 bg-amber-50 rounded border border-amber-200"
                                          data-testid={`goal-pending-${node.id}`}
                                        >
                                          <div className="w-2 h-2 bg-amber-400 rounded-full flex-shrink-0" />
                                          <div className="flex-1 min-w-0">
                                            <span className="text-sm font-medium text-amber-800">{node.name}</span>
                                            {parentNode && (
                                              <span className="text-xs text-amber-600 ml-2">({parentNode.name})</span>
                                            )}
                                          </div>
                                          <span className="text-xs text-amber-600">Sin meta</span>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-7 text-xs"
                                            onClick={() => {
                                              setGoalForm({
                                                kpi_id: kpi.id,
                                                year: selectedYear,
                                                bimester: bucketIndex,
                                                bimester_goal: '',  // Empty string for better UX
                                                node_id: node.id
                                              });
                                              setSelectedKpiForGoal(kpi);
                                              setEditingGoal(null);
                                              setIsGoalDialogOpen(true);
                                            }}
                                          >
                                            <Plus className="w-3 h-3 mr-1" />
                                            Agregar
                                          </Button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })}
                            </>
                          )}
                        </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Tab */}
          <TabsContent value="audit">
            <Card data-testid="audit-settings-card">
              <CardHeader>
                <CardTitle className="font-['Manrope'] flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Registro de Auditoría
                </CardTitle>
                <CardDescription>
                  Historial de cambios realizados en el sistema
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {auditLogs.length === 0 ? (
                  <div className="text-center py-12">
                    <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No hay registros de auditoría</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                    {auditLogs.map((log) => (
                      <div key={log.id} className="p-4 hover:bg-slate-50" data-testid={`audit-row-${log.id}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{getEntityLabel(log.entity_type)}</Badge>
                              <span className="text-sm font-medium text-slate-900">
                                {getActionLabel(log.action)}
                              </span>
                            </div>
                            <p className="text-sm text-slate-500 mt-1">
                              Por: {log.user_name}
                            </p>
                            {log.reason && (
                              <p className="text-sm text-amber-600 mt-1">
                                Motivo: {log.reason}
                              </p>
                            )}
                            {log.old_value && log.new_value && (
                              <p className="text-xs text-slate-400 mt-1">
                                Anterior: {JSON.stringify(log.old_value).slice(0, 50)}... → Nuevo: {JSON.stringify(log.new_value).slice(0, 50)}...
                              </p>
                            )}
                          </div>
                          <span className="text-xs text-slate-400">
                            {formatDate(log.timestamp)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Generate/Regenerate Periods Dialog */}
        <Dialog open={isGenerateDialogOpen} onOpenChange={(open) => {
          if (!generating && !isRegenerating) {
            setIsGenerateDialogOpen(open);
          }
        }}>
          <DialogContent data-testid="generate-periods-dialog" className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">
                {periods.length > 0 ? 'Regenerar Periodos' : 'Configurar y Generar Periodos'}
              </DialogTitle>
              <DialogDescription>
                {periods.length > 0 
                  ? `Se eliminarán los ${periods.length} periodos existentes y las metas asociadas del año ${selectedYear}.`
                  : `Configure el calendario para el año ${selectedYear}.`
                }
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* PER inicial */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="initial-per">PER Inicial</Label>
                  <Input
                    id="initial-per"
                    type="number"
                    min="1"
                    max="52"
                    value={configForm.initial_per_number}
                    onChange={(e) => setConfigForm(prev => ({ 
                      ...prev, 
                      initial_per_number: parseInt(e.target.value) || 1 
                    }))}
                    data-testid="initial-per-input"
                  />
                  <p className="text-xs text-slate-500">Ej: 3, 5, etc.</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="per-type">Tipo de PER Inicial</Label>
                  <Select 
                    value={configForm.initial_per_type} 
                    onValueChange={(v) => setConfigForm(prev => ({ ...prev, initial_per_type: v }))}
                  >
                    <SelectTrigger id="per-type" data-testid="per-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="special">Especial</SelectItem>
                      <SelectItem value="standard">Estándar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Fecha de inicio */}
              <div className="space-y-2">
                <Label htmlFor="start-date">Fecha de Inicio</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={configForm.start_date}
                  onChange={(e) => setConfigForm(prev => ({ ...prev, start_date: e.target.value }))}
                  min={`${selectedYear}-01-01`}
                  max={`${selectedYear}-12-31`}
                  data-testid="period-start-date-input"
                />
                <p className="text-xs text-slate-500">
                  {configForm.initial_per_type === 'standard' 
                    ? 'Si no es jueves, se usará el primer jueves posterior.'
                    : 'Fecha exacta de inicio del periodo especial.'
                  }
                </p>
              </div>
              
              {/* Duración del PER especial */}
              {configForm.initial_per_type === 'special' && (
                <div className="space-y-2">
                  <Label htmlFor="special-duration">Duración del PER Especial (días)</Label>
                  <Input
                    id="special-duration"
                    type="number"
                    min="1"
                    max="14"
                    value={configForm.special_duration}
                    onChange={(e) => setConfigForm(prev => ({ 
                      ...prev, 
                      special_duration: parseInt(e.target.value) || 1 
                    }))}
                    data-testid="special-duration-input"
                  />
                  <p className="text-xs text-slate-500">Por defecto: 3 días</p>
                </div>
              )}
              
              {/* Vista previa */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-700 mb-2">Vista previa:</p>
                <ul className="text-xs text-slate-600 space-y-1">
                  <li>
                    • <strong>PER {configForm.initial_per_number}:</strong>{' '}
                    {configForm.start_date 
                      ? new Date(configForm.start_date + 'T12:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })
                      : '(fecha)'
                    }{' '}
                    ({configForm.initial_per_type === 'special' 
                      ? `${configForm.special_duration} días, especial` 
                      : '7 días, jueves a miércoles'
                    })
                  </li>
                  <li>• <strong>PER {configForm.initial_per_number + 1}+:</strong> Jueves a miércoles (7 días c/u)</li>
                </ul>
              </div>
              
              {/* Advertencia para regeneración */}
              {periods.length > 0 && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    <span><strong>Advertencia:</strong> Se eliminarán las metas del {selectedYear}.</span>
                  </p>
                </div>
              )}
            </div>
            
            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                variant="outline" 
                onClick={() => setIsGenerateDialogOpen(false)}
                disabled={generating || isRegenerating}
              >
                Cancelar
              </Button>
              {periods.length > 0 ? (
                <Button 
                  onClick={handleRegeneratePeriods} 
                  disabled={generating || isRegenerating || !configForm.start_date}
                  variant="destructive"
                  data-testid="confirm-regenerate-btn"
                >
                  {isRegenerating ? 'Regenerando...' : 'Regenerar'}
                </Button>
              ) : (
                <Button 
                  onClick={handleGeneratePeriods} 
                  disabled={generating || isRegenerating || !configForm.start_date} 
                  data-testid="confirm-generate-btn"
                >
                  {generating ? 'Generando...' : 'Generar'}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add/Edit Goal Dialog */}
        <Dialog 
          open={isGoalDialogOpen} 
          onOpenChange={(open) => {
            // Prevent closing while saving
            if (savingGoal) return;
            setIsGoalDialogOpen(open);
            if (!open) {
              setSelectedKpiForGoal(null);
              setEditingGoal(null);
            }
          }}
        >
          <DialogContent 
            data-testid="goal-dialog"
            onPointerDownOutside={(e) => {
              // Prevent closing on outside click while saving
              if (savingGoal) e.preventDefault();
            }}
            onEscapeKeyDown={(e) => {
              // Prevent closing on ESC while saving
              if (savingGoal) e.preventDefault();
            }}
          >
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">
                {editingGoal 
                  ? `Editar Meta ${getBucketTermLabel(selectedKpiForGoal?.periodicity || 'weekly', selectedKpiForGoal?.goal_periodicity || 'bimonthly')}` 
                  : `Agregar Meta ${getBucketTermLabel(selectedKpiForGoal?.periodicity || 'weekly', selectedKpiForGoal?.goal_periodicity || 'bimonthly')}`}
              </DialogTitle>
              <DialogDescription>
                {editingGoal 
                  ? 'Modifica la meta. Los cambios se registrarán en auditoría.'
                  : goalForm.node_id 
                    ? 'Define la meta para este nodo. Los padres se calcularán automáticamente.'
                    : selectedKpiForGoal?.periodicity === 'weekly' || selectedKpiForGoal?.periodicity === 'bimonthly' || !selectedKpiForGoal
                      ? `La meta se distribuirá automáticamente entre los periodos del ${(selectedKpiForGoal?.goal_periodicity === 'quarterly' ? 'trimestre' : (selectedKpiForGoal?.goal_periodicity === 'monthly' ? 'mes' : 'bimestre'))}`
                      : `Define la meta para el ${getBucketTermLabel(selectedKpiForGoal?.periodicity, selectedKpiForGoal?.goal_periodicity).toLowerCase()} seleccionado`}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSaveGoal} className="space-y-4">
              <div className="space-y-2">
                <Label>KPI</Label>
                <Select
                  value={goalForm.kpi_id}
                  onValueChange={handleKpiSelect}
                  disabled={!!editingGoal || !!goalForm.node_id}
                >
                  <SelectTrigger data-testid="goal-kpi-select">
                    <SelectValue placeholder="Selecciona un KPI" />
                  </SelectTrigger>
                  <SelectContent>
                    {displayKpis.map((kpi) => {
                      const periodLabel = kpi.periodicity && kpi.periodicity !== 'weekly' && kpi.periodicity !== 'bimonthly' 
                        ? ` [${getPeriodicityLabel(kpi.periodicity)}]` 
                        : '';
                      return (
                        <SelectItem key={kpi.id} value={kpi.id}>
                          <span className="uppercase">{kpi.name}</span>
                          {kpi.has_hierarchy && ' (Jerárquico)'}
                          {periodLabel && <span className="text-blue-600">{periodLabel}</span>}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Show node selector for hierarchical KPIs */}
              {selectedKpiForGoal?.has_hierarchy && selectedKpiForGoal?.hierarchy_nodes && !editingGoal && (
                <div className="space-y-2">
                  <Label>Nodo (Estrategia)</Label>
                  <Select
                    value={goalForm.node_id || ''}
                    onValueChange={(v) => setGoalForm({ ...goalForm, node_id: v })}
                    disabled={!!editingGoal}
                  >
                    <SelectTrigger data-testid="goal-node-select">
                      <SelectValue placeholder="Selecciona un nodo" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedKpiForGoal.hierarchy_nodes
                        .filter(n => n.is_leaf)
                        .map((node) => {
                          const parentNode = selectedKpiForGoal.hierarchy_nodes.find(p => p.id === node.parent_id);
                          return (
                            <SelectItem key={node.id} value={node.id}>
                              {node.name} {parentNode && `(${parentNode.name})`}
                            </SelectItem>
                          );
                        })}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    Solo puedes definir metas en nodos hoja. Los padres se calculan automáticamente.
                  </p>
                </div>
              )}
              
              {/* Show selected node info when editing */}
              {editingGoal?.node_id && (
                <div className="p-3 bg-violet-50 rounded-lg border border-violet-200">
                  <div className="flex items-center gap-2">
                    <GitBranch className="w-4 h-4 text-violet-600" />
                    <span className="text-sm font-medium text-violet-800">
                      Nodo: {editingGoal.node_name}
                    </span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Año</Label>
                  <Select
                    value={goalForm.year.toString()}
                    onValueChange={(v) => setGoalForm({ ...goalForm, year: parseInt(v) })}
                    disabled={!!editingGoal}
                  >
                    <SelectTrigger data-testid="goal-year-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2026">2026</SelectItem>
                      <SelectItem value="2025">2025</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{getBucketTermLabel(selectedKpiForGoal?.periodicity || 'weekly', selectedKpiForGoal?.goal_periodicity || 'bimonthly')}</Label>
                  <Select
                    value={goalForm.bimester.toString()}
                    onValueChange={(v) => setGoalForm({ ...goalForm, bimester: parseInt(v) })}
                    disabled={!!editingGoal}
                  >
                    <SelectTrigger data-testid="goal-bimester-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getBucketOptions(
                        selectedKpiForGoal?.periodicity || 'weekly',
                        selectedKpiForGoal?.periodicity_start_month || 1,
                        selectedKpiForGoal?.goal_periodicity || 'bimonthly'
                      ).map((opt) => (
                        <SelectItem key={opt.value} value={opt.value.toString()}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                {selectedKpiForGoal?.goal_calculation_type === 'range' ? (
                  <>
                    <Label>Rango de meta (Mínimo – Máximo)</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Mínimo</Label>
                        <Input
                          type="number"
                          step="any"
                          value={goalForm.goal_range_min}
                          onChange={(e) => setGoalForm({ ...goalForm, goal_range_min: e.target.value })}
                          placeholder="Ej: 60"
                          data-testid="goal-range-min-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Máximo</Label>
                        <Input
                          type="number"
                          step="any"
                          value={goalForm.goal_range_max}
                          onChange={(e) => setGoalForm({ ...goalForm, goal_range_max: e.target.value })}
                          placeholder="Ej: 66"
                          data-testid="goal-range-max-input"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">
                      El resultado será verde (en meta) si cae entre el mínimo y el máximo, incluidos ambos extremos. Fuera del rango será rojo.
                    </p>
                    {editingGoal && editingGoal.goal_range && (
                      <p className="text-xs text-slate-500">
                        Rango anterior: {formatNumber(editingGoal.goal_range.min)} – {formatNumber(editingGoal.goal_range.max)}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <Label>Meta {selectedKpiForGoal?.periodicity === 'monthly' ? 'Mensual' : selectedKpiForGoal?.periodicity === 'semiannual' ? 'Semestral' : (selectedKpiForGoal?.goal_periodicity === 'quarterly' ? 'Trimestral' : (selectedKpiForGoal?.goal_periodicity === 'monthly' ? 'Mensual' : 'Bimestral'))}</Label>
                    <Input
                      type="number"
                      min="0"
                      step="any"
                      value={goalForm.bimester_goal}
                      onChange={(e) => setGoalForm({ ...goalForm, bimester_goal: e.target.value })}
                      placeholder="0"
                      data-testid="goal-value-input"
                    />
                    <p className="text-xs text-slate-500">
                      Acepta valores decimales (ej: 0.5, 0.75, 1.25). Para tasas o porcentajes, use el valor decimal correspondiente.
                    </p>
                    {editingGoal && (
                      <p className="text-xs text-slate-500">
                        Valor anterior: {formatNumber(editingGoal.bimester_goal)}
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Historical goal edit - requires reason */}
              {editingGoal && isHistoricalGoal && (
                <div className="space-y-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Meta histórica</p>
                      <p className="text-xs text-amber-600 mt-1">
                        Esta meta tiene resultados capturados. Se requiere un motivo para modificarla.
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-amber-800">Etiqueta *</Label>
                    <Select
                      value={goalEditLabel}
                      onValueChange={setGoalEditLabel}
                    >
                      <SelectTrigger className="border-amber-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="informativo">Informativo</SelectItem>
                        <SelectItem value="desviacion">Desviación</SelectItem>
                        <SelectItem value="accion_tomada">Plan de Acción</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-amber-800">Motivo del cambio *</Label>
                    <Textarea
                      value={goalEditReason}
                      onChange={(e) => setGoalEditReason(e.target.value)}
                      placeholder="Explique el motivo de la corrección de esta meta histórica (mínimo 5 caracteres)"
                      className="border-amber-300 min-h-[80px]"
                      data-testid="goal-edit-reason"
                    />
                    {goalEditReason && goalEditReason.length < 5 && (
                      <p className="text-xs text-red-500">
                        El motivo debe tener al menos 5 caracteres ({goalEditReason.length}/5)
                      </p>
                    )}
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsGoalDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  disabled={savingGoal || (!goalForm.kpi_id) || (selectedKpiForGoal?.has_hierarchy && !goalForm.node_id && !editingGoal)} 
                  data-testid="save-goal-btn"
                >
                  {savingGoal ? 'Guardando...' : editingGoal ? 'Actualizar Meta' : 'Guardar Meta'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Goal Dialog */}
        <AlertDialog open={deleteGoalDialogOpen} onOpenChange={setDeleteGoalDialogOpen}>
          <AlertDialogContent data-testid="delete-goal-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-['Manrope'] flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-rose-600" />
                Eliminar Meta
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-3">
                <p>
                  ¿Estás seguro de que deseas eliminar esta meta?
                </p>
                {goalToDelete && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <div className="space-y-1 text-sm">
                      <p><strong>KPI:</strong> {goalToDelete.kpiName}</p>
                      <p><strong>Bimestre:</strong> {getBimesterName(goalToDelete.bimester)}</p>
                      <p><strong>Meta:</strong> {formatNumber(goalToDelete.bimester_goal)}</p>
                      {goalToDelete.node_name && (
                        <p><strong>Nodo:</strong> {goalToDelete.node_name}</p>
                      )}
                    </div>
                  </div>
                )}
                <p className="text-sm text-slate-500">
                  Esta acción no afectará los resultados capturados.
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletingGoal}>
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteGoal}
                disabled={deletingGoal}
                className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
                data-testid="confirm-delete-goal-btn"
              >
                {deletingGoal ? 'Eliminando...' : 'Eliminar Meta'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default Settings;
